import { Box, Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { CONTENT_BLOCK_MAX_WIDTH, CONTENT_BLOCK_PADDING__X, getBrannanMobileMediaQuery } from '../../LayoutAloha.theme';
import { StyledTitle } from './Page.styles';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import { ResponsiveText } from '@apps/guest/packages/layout-engine/components/ResponsiveText';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { Category, DesignLayoutType, EventPageType, EventPageVisibility, PhotoFragment } from '@graphql/generated';
import CoverPhoto from '../../../../components/CoverPhoto';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useFeatureValue } from '@shared/core/featureFlags';
import { InlineEditor } from '@shared/components';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
interface Props
  extends Readonly<{
    title?: Maybe<string>;
    subtitle?: Maybe<string>;
    graphicAccent?: Maybe<JSX.Element>;
    marginTop?: string | string[];
    isAccommodations?: boolean;
    pageSlug?: string;
    photo?: Maybe<PhotoFragment>;
    pageId: string;
    pageType: EventPageType;
    visibility: EventPageVisibility;
    isPageContainer?: boolean;
  }> {}

export const Page = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>((props, pageRef) => {
  const { title, subtitle, graphicAccent, children, isAccommodations, pageSlug, photo, pageId, pageType, visibility, isPageContainer, ...restProps } = props;

  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const alohaMobilePagePhotoEnabled = useFeatureValue('alohaMobilePagePhotoEnabled').value === 'on';
  const isPreviewing = isInIframe();
  const { pageFontColor } = useColorPaletteProvider();

  const { t } = useTranslation('joykit');
  const inlineEditorTrans = t('pageNote');

  const onInlineEditClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editPageNote',
        inlineEditData: { pageId, isPageContainer: isPageContainer || false, pageSubTitle: subtitle || '' },
      },
    });
  });

  return isAccommodations ? (
    <Flex
      ref={pageRef}
      {...restProps}
      maxWidth={'700px'}
      width="100%"
      justifyContent={'center'}
      flexDirection={'column'}
    >
      {alohaMobilePagePhotoEnabled && isSmallScreen && photo && (
        <Box paddingBottom={10}>
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title && (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <ResponsiveText
            typographyVariant="display3"
            defaultFontSize={36}
            marginTop={5}
            textAlign={'center'}
            wordBreak="break-word"
            text={title}
            skipResize={'Accommodations' !== title}
            fontCategory={Category.PAGE_TITLE}
          />
        </GuestSiteTypographyOverride>
      )}
      <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
        <TextV2
          as="div"
          typographyVariant="body4"
          marginTop={subtitle || isPreviewing ? 8 : 0}
          paddingX={7}
          marginX={'auto'}
          textAlign={'center'}
          maxWidth={'500px'}
          wordBreak="break-word"
          color={pageFontColor || 'mono14'}
        >
          <InlineEditor
            actionData={{ edit: onInlineEditClick }}
            wrapperType="actionOutside"
            componentName="pageNote"
            elementLabel={inlineEditorTrans.title()}
            pageName={pageType}
            pageSlug={pageSlug || ''}
            stickyOnScroll={true}
            wrapperCSS={{ zIndex: 30 }}
            isEligibleForInlineEditing={true}
            showIntro
          >
            {subtitle ? (
              <PrettyLink
                source={pageSlug}
                allowedTypes={['EMAIL']}
              >
                {subtitle}
              </PrettyLink>
            ) : (
              isPreviewing && (
                <TextV2
                  opacity={0.7}
                  fontStyle="italic"
                >
                  {inlineEditorTrans.defaultNote()}
                </TextV2>
              )
            )}
          </InlineEditor>
        </TextV2>
      </GuestSiteTypographyOverride>
      <AccommodationsCountdownExperiment />
      {children}
    </Flex>
  ) : (
    <Box
      ref={pageRef}
      {...restProps}
      maxWidth={{ _: '700px', lg: '614px' }}
      marginX={'auto'}
    >
      {alohaMobilePagePhotoEnabled && isSmallScreen && photo && (
        <Box paddingBottom={10}>
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title ? (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <StyledTitle
            typographyVariant="display3"
            marginTop={5}
            textAlign={'center'}
          >
            {title}
          </StyledTitle>
        </GuestSiteTypographyOverride>
      ) : null}
      {subtitle ? (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={5}
            marginX={'auto'}
            textAlign={'center'}
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            paddingX={CONTENT_BLOCK_PADDING__X}
            wordBreak="break-word"
          >
            <PrettyLink source={pageSlug}>{subtitle}</PrettyLink>
          </TextV2>
        </GuestSiteTypographyOverride>
      ) : null}
      {children}
    </Box>
  );
});
